import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { X, PhoneCall, FileText, Clock, ChevronDown } from "lucide-react";
import StatusBadge, { STATUS_LIST, STATUS_LABELS } from "@/components/StatusBadge";
import CallButton from "@/components/CallButton";

export default function ContactTimeline({ contactId, onClose }) {
  const { lang } = useI18n();
  const L = lang === "el";
  const [data, setData] = useState(null);
  const [showChangeForm, setShowChangeForm] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/contacts/${contactId}/timeline`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  const timeline = useMemo(() => {
    if (!data) return [];
    const calls = (data.calls || []).map((c) => ({
      type: "call", at: c.date, ...c,
    }));
    const events = (data.events || []).map((e) => ({
      type: "event", at: e.created_at, ...e,
    }));
    return [...calls, ...events].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [data]);

  if (!data) {
    return (
      <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-8 text-sm text-slate-500">{L ? "Φόρτωση..." : "Loading..."}</div>
      </div>
    );
  }

  const c = data.contact;

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="contact-timeline-modal">
        {/* Header */}
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Ιστορικό πελάτη" : "Customer history"}</div>
            <h2 className="font-heading text-xl font-bold text-slate-900 truncate">
              {c.company_name || `${c.first_name} ${c.last_name}`}
            </h2>
            <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
              <span>{c.first_name} {c.last_name}</span>
              {c.phone && <span>· {c.phone}</span>}
              {c.email && <span>· {c.email}</span>}
              {c.phone && <CallButton phone={c.phone} contactId={c.id} size="xs" />}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge status={c.pipeline_status || "new"} />
              {c.next_action_at && (
                <span className="text-xs text-slate-600">
                  <Clock className="inline w-3 h-3 mr-1" />
                  {new Date(c.next_action_at).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </header>

        {/* Action bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap gap-2">
          {!showChangeForm ? (
            <button
              data-testid="timeline-add-status-btn"
              onClick={() => setShowChangeForm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0052CC] text-white rounded-md text-sm font-semibold"
            >
              <ChevronDown className="w-4 h-4" /> {L ? "Καταγραφή ενέργειας" : "Log action"}
            </button>
          ) : (
            <StatusChangeForm
              contact={c}
              onCancel={() => setShowChangeForm(false)}
              onSaved={() => { setShowChangeForm(false); load(); }}
            />
          )}
        </div>

        {/* Timeline */}
        <div className="px-6 py-4 overflow-y-auto flex-1" data-testid="contact-timeline-list">
          {timeline.length === 0 ? (
            <div className="text-center text-slate-400 py-12 text-sm">{L ? "Δεν υπάρχουν συμβάντα ακόμη." : "No events yet."}</div>
          ) : (
            <ul className="space-y-3">
              {timeline.map((item, idx) => (
                <li key={item.id || idx} className="flex gap-3">
                  <div className="flex flex-col items-center pt-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.type === "call" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
                      {item.type === "call" ? <PhoneCall className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                    </div>
                    {idx < timeline.length - 1 && <div className="flex-1 w-px bg-slate-200 my-1" />}
                  </div>
                  <div className="flex-1 pb-2">
                    {item.type === "call" ? (
                      <CallItem call={item} L={L} />
                    ) : (
                      <EventItem ev={item} L={L} />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function CallItem({ call, L }) {
  const outcomeLabel = (o) => ({
    no_answer: L ? "Δεν απάντησε" : "No answer",
    callback_requested: L ? "Ζήτησε κλήση" : "Callback",
    interested: L ? "Ενδιαφερόμενος" : "Interested",
    not_interested: L ? "Δεν ενδιαφέρεται" : "Not interested",
    meeting_scheduled: L ? "Συνάντηση" : "Meeting",
    proposal_requested: L ? "Πρόταση" : "Proposal",
    deal_won: L ? "Κερδισμένη" : "Won",
    deal_lost: L ? "Χαμένη" : "Lost",
  }[o] || o);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <span className="text-xs font-semibold text-slate-900">{L ? "Κλήση" : "Call"}</span>
          {call.twilio_direction && <span className="ml-1 text-[10px] uppercase text-slate-500">· {call.twilio_direction}</span>}
          <span className="text-xs text-slate-600 ml-2">{outcomeLabel(call.outcome)}</span>
          <span className="text-xs text-slate-500 ml-2">· {call.duration_minutes}m</span>
        </div>
        <span className="text-[11px] text-slate-500">{new Date(call.date).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      {call.notes && <div className="text-sm text-slate-700 mt-1">{call.notes}</div>}
      {call.actor_name && <div className="text-[10px] text-slate-500 mt-1">{call.actor_name}</div>}
    </div>
  );
}

function EventItem({ ev, L }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-900">{L ? "Αλλαγή κατάστασης" : "Status change"}</span>
          {ev.from_status && <StatusBadge status={ev.from_status} />}
          <span className="text-slate-400 text-xs">→</span>
          {ev.to_status && <StatusBadge status={ev.to_status} />}
        </div>
        <span className="text-[11px] text-slate-500">{new Date(ev.created_at).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      {ev.reason && <div className="text-sm text-slate-700 mt-1"><strong>{L ? "Λόγος:" : "Reason:"}</strong> {ev.reason}</div>}
      {ev.note && <div className="text-sm text-slate-700 mt-1">{ev.note}</div>}
      {ev.next_action_at && (
        <div className="text-xs text-slate-600 mt-1">
          <Clock className="inline w-3 h-3 mr-1" />
          {L ? "Επόμενη ενέργεια:" : "Next action:"} {new Date(ev.next_action_at).toLocaleString(L ? "el-GR" : "en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
      {ev.actor_name && <div className="text-[10px] text-slate-500 mt-1">{ev.actor_name}</div>}
    </div>
  );
}

function StatusChangeForm({ contact, onCancel, onSaved }) {
  const { lang } = useI18n();
  const L = lang === "el";
  const [status, setStatus] = useState("called");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [nextAt, setNextAt] = useState("");
  const [saving, setSaving] = useState(false);

  const needsNextDate = ["callback_scheduled", "meeting_scheduled"].includes(status);
  const needsReason = ["not_interested", "lost", "won"].includes(status);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/contacts/${contact.id}/status`, {
        pipeline_status: status,
        status_reason: reason || null,
        note: note || null,
        next_action_at: nextAt ? new Date(nextAt).toISOString() : null,
      });
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 w-full" data-testid="status-change-form">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">{L ? "Κατάσταση" : "Status"}</label>
        <select data-testid="status-change-select" value={status} onChange={(e) => setStatus(e.target.value)} className="block mt-1 px-2 py-1.5 border border-slate-300 rounded text-sm bg-white">
          {STATUS_LIST.map((s) => (
            <option key={s} value={s}>{L ? STATUS_LABELS[s].el : STATUS_LABELS[s].en}</option>
          ))}
        </select>
      </div>
      {needsNextDate && (
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">{L ? "Πότε" : "When"}</label>
          <input
            data-testid="status-change-next-at"
            type="datetime-local"
            required
            value={nextAt}
            onChange={(e) => setNextAt(e.target.value)}
            className="block mt-1 px-2 py-1.5 border border-slate-300 rounded text-sm bg-white"
          />
        </div>
      )}
      <div className="flex-1 min-w-[200px]">
        <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600">
          {needsReason ? (L ? "Λόγος *" : "Reason *") : (L ? "Σημείωση" : "Note")}
        </label>
        <input
          data-testid="status-change-reason"
          type="text"
          required={needsReason}
          value={needsReason ? reason : note}
          onChange={(e) => needsReason ? setReason(e.target.value) : setNote(e.target.value)}
          placeholder={needsReason ? (L ? "Γιατί;" : "Why?") : (L ? "π.χ. Στείλαμε προσφορά" : "e.g. Sent proposal")}
          className="block mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm"
        />
      </div>
      <div className="flex gap-2 items-end">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 border border-slate-300 rounded text-xs">{L ? "Άκυρο" : "Cancel"}</button>
        <button type="submit" data-testid="status-change-submit" disabled={saving} className="px-3 py-1.5 bg-[#0052CC] text-white rounded text-xs font-semibold disabled:opacity-50">
          {saving ? "..." : (L ? "Αποθήκευση" : "Save")}
        </button>
      </div>
    </form>
  );
}
